/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {ReceiptPercentIcon, RocketLaunchIcon, UsersIcon} from "@heroicons/react/24/outline";
import {floatNumberFormat} from "utils/utility";
import {PackageCategoryEntity} from "types/entities/PackageCategoryEntity";
import {CalendarDaysIcon} from "@heroicons/react/24/solid";
import MicroSlider from "pages/Front/components/MicroSlider";
import {PackageCategory} from "types/enums/PackageCategory";

interface PackageCategoryCardProps {
    categoryEntity: PackageCategoryEntity;
    className: string;
    onClick: () => void;
    onDetailClick: () => void;
    index: number;
    showMoreDetail: boolean;
}

const PackageCategoryCard = (props: PackageCategoryCardProps) => {
    const {t} = useTranslation();

    const categoryNames = props.categoryEntity.title.split(" ");

    const categoryNameTooLong = props.categoryEntity.title.length > 15;

    return (
        <article
            className={clsx(
                "PackageCategoryCard font-poppins snap-center bg-white shadow-lg rounded-2xl w-full bg-cover bg-no-repeat bg-center p-4 lg:p-5",
                "grid md:grid-cols-[130px,130px,1fr,1fr,0.7fr] lg:grid-cols-[130px,130px,1fr,1fr,0.7fr] xl:grid-cols-[146px,146px,1fr,1fr,1fr] gap-4 gap-y-6 sm:gap-y-4 md:gap-x-6 lg:gap-x-8 xl:gap-x-16 md:grid-rows-[auto,1fr,auto]",
                "PackageCategory-" + props.categoryEntity.id,
                props.className
            )}
        >
            <section className={"order-2 md:order-1 col-span-1 md:col-span-2 md:row-span-2 PackageCategorySlider"}>
                <div className={"MediaSliderHolder w-full" + " sm:max-w-md md:max-w-full mx-auto"}>
                    <MicroSlider duration={10000} images={props.categoryEntity.images} position={props.index} />
                </div>
            </section>

            <section className={"order-1 md:order-2 md:row-span-1 col-span-1 md:col-span-3 CategoryName"}>
                <div
                    className={"select-none cursor-pointer space-y-4 pt-1 lg:space-y-6 xl:space-y-8 text-base"}
                    id={"PackageCategoryTitle:" + props.categoryEntity.id}
                    onClick={props.onClick}
                >
                    <p className={"font-semibold leading-tight md:leading-normal text-sm sm:text-base text-gray-600 text-center md:text-left"}>
                        {props.categoryEntity.shortTitle}
                    </p>
                    <div
                        className={clsx(
                            props.categoryEntity.id === PackageCategory.School ? "flex-col sm:flex-row " : "flex-row",
                            categoryNameTooLong
                                ? "text-[2.2rem] sm:text-[2.8rem] md:text-[3rem] lg:text-[4rem] xl:text-[5.5rem] max-w-md sm:max-w-md lg:max-w-full"
                                : "text-[2.8rem] sm:text-[3rem] md:text-[4rem] lg:text-[5rem] xl:text-[5.5rem]",

                            "CategoryTitle font-zuume font-bold italic sm:text-left text-center  tracking-wide leading-[0.85] flex  justify-center md:justify-start space-x-3 sm:space-x-3"
                        )}
                    >
                        <span className={"text-arbo-red"}>{categoryNames[0]}</span>
                        <span className={"text-arbo-yellow"}>
                            {categoryNames[1]} {categoryNames[2]}
                        </span>
                    </div>
                </div>
            </section>

            <section className={"order-3 md:order-3 md:row-span-1 col-span-1 md:col-span-3"}>
                <div className={"text-sm lg:text-base select-none text-gray-800 sm:max-w-xl md:max-w-full mx-auto"}>
                    <div className={"md:pr-8 whitespace-pre-line text-wrap"}>{props.categoryEntity.description}</div>
                </div>
            </section>
            <section
                className={
                    "order-5 md:order-6 col-start-1 row-span-1 md:row-start-3 md:col-end-3 col-span-1 md:col-span-2 place-content-end PackageCategoryPricing"
                }
            >
                <div className={"select-none rounded-2xl gap-y-2 flex flex-col leading-[1] text-center sm:max-w-md md:max-w-full mx-auto"}>
                    <div
                        className={
                            "cursor-pointer rounded-2xl text-gray-500 text-sm bg-gradient-to-b from-[#f7f7f7] to-white pt-5 pb-4 capitalize font-semibold px-2 lg:px-4 h-full flex flex-row space-x-3 justify-center items-end"
                        }
                        id={"PricePerPerson:" + props.categoryEntity.id}
                        onClick={props.onClick}
                    >
                        <div>{t("from")}</div>
                        <div className={"flex font-zuume text-black text-5xl xl:text-[45pt] font-bold italic"}>
                            <span className={"-mb-2 sm:-mb-3"}>{floatNumberFormat(props.categoryEntity.price * 100)} CHF</span>
                        </div>
                        <div className={"PerPerson"}>{t("per person")}</div>
                    </div>

                    <button
                        className={clsx(
                            "ButtonCTA duration-150 transform-gpu",

                            " rounded-2xl hover:drop-shadow-none drop-shadow-[0px_5px_5px_rgba(0,0,0,0.20)] mix-blend-multiply bg-gradient-to-l from-[#F7C12A] to-[#ED3D34] hover:text-white text-black text-lg sm:text-xl lg:text-2xl xl:text-3xl py-3 font-bold"
                        )}
                        id={"BookNow:" + props.categoryEntity.id}
                        onClick={props.onClick}
                    >
                        <span>{props.categoryEntity.buttonText}</span>
                    </button>
                </div>
            </section>
            <section className={"DescriptionTags order-4 md:order-4 md:col-start-3 md:row-start-3 col-span-1 md:col-span-3 md:row-span-1  place-content-end"}>
                <div
                    className={
                        "select-none flex flex-col items-center justify-center md:justify-start sm:flex-row gap-2 md:gap-5 w-full sm:max-w-md md:max-w-full mx-auto"
                    }
                >
                    {/*<div className={"flex flex-row shrink grow gap-2 lg:gap-x-3 xl:gap-x-5 justify-between sm:justify-start"}>*/}
                    <div className={"grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-x-3 xl:gap-x-4 justify-items-stretch"}>
                        {props.categoryEntity.tags.map((tag, index) => {
                            const getTagIcon = () => {
                                switch (tag.iconName) {
                                    case "calendar":
                                        return <CalendarDaysIcon className={"size-5 md:size-6 flex-none"} />;
                                    case "persons":
                                        return <UsersIcon className={"size-5 md:size-6  flex-none"} />;
                                    case "percent":
                                        return <ReceiptPercentIcon className={"size-5 md:size-6  flex-none"} />;
                                    case "activities":
                                        return <RocketLaunchIcon className={"size-5 md:size-6  flex-none"} />;
                                }
                            };
                            return (
                                <div
                                    key={index}
                                    className={
                                        "DescriptionTag sm:w-auto md:max-w-[10rem] lg:max-w-[12rem] flex flex-col sm:flex-auto items-center justify-center p-1.5 xl:p-2.5 rounded-lg border-2 border-gray-100 "
                                    }
                                >
                                    <div className={"DescriptionTagIcon pb-2 text-gray-600"}>{getTagIcon()}</div>
                                    <span className={"text-xs xl:text-sm text-center text-black"}>{tag.title}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
            {props.showMoreDetail && props.categoryEntity.id !== PackageCategory.Giftcard && (
                <section
                    className={
                        "order-6 md:order-5 col-span-1 md:col-span-1 md:col-start-5 md:row-start-3 md:row-span-1 DescriptionMoreDetail place-content-end place-items-center lg:place-items-start  border-t-2 md:border-0 pt-4 md:pt-0 border-gray-100"
                    }
                >
                    <div className={"grow flex flex-row items-center justify-center select-none"}>
                        <button
                            className={
                                "MoreDetailsBtn flex flex-row sm:flex-col items-center justify-between sm:justify-around sm:gap-y-1 gap-x-3 sm:gap-x-0 sm:h-full "
                            }
                            type={"button"}
                            onClick={props.onDetailClick}
                        >
                            <div
                                className={
                                    "font-poppins text-4xl flex items-center justify-center size-12  md:size-14 mix-blend-multiply bg-gradient-to-r from-[#F7C12A] to-[#ED3D34] text-white rounded-full"
                                }
                            >
                                <span>i</span>
                            </div>
                            <span className={"text-xs xl:text-sm leading-4 text-black py-3 md:pt-0 px-2"}>{t("More details")}</span>
                        </button>
                    </div>
                </section>
            )}
        </article>
    );
};

export default PackageCategoryCard;
